// Pure data model, constants and helpers for the sandbox portal.
// Extracted from SandboxPage so the redesign's shared components can import
// them without a dependency cycle (SandboxPage re-exports everything here).

export type SandboxStatus =
  | 'requested'
  | 'planning_account'
  | 'account_planned'
  | 'creating_account'
  | 'account_created'
  | 'bootstrapping_account'
  | 'preparing_state'
  | 'planning'
  | 'planned'
  | 'applying'
  | 'awaiting_dns'
  | 'destroying'
  | 'ready'
  | 'failed'
  | 'destroyed';

export type RetentionOption = '7d' | '30d' | '90d' | '365d' | 'never';

type ManualDnsRecord = {
  name: string;
  type: string;
  value: string;
  purpose?: string;
};

export type SandboxOutputs = {
  stateKey: string;
  tfvarsPath: string;
  githubRoleArn: string;
  deployBucket: string;
  sandboxUrl: string;
  sandboxApiUrl?: string;
  sandboxWebUrl?: string;
  awsSsoLoginCommand?: string;
  databaseTunnelCommand?: string;
  databasePasswordCommand?: string;
  databaseConnectCommand?: string;
  managedAsgName?: string;
  managedAsgDnsName?: string;
  ecrUri?: string;
  webBucket?: string;
  cloudfrontDistributionId?: string;
  manualDnsRecords?: ManualDnsRecord[];
  lambdaArn?: string;
  bastionInstanceId?: string;
  bastionPublicIp?: string;
  bastionPrivateIp?: string;
  bastionSsmTarget?: string;
  databaseEndpoint?: string;
  databasePort?: string;
  databaseName?: string;
  databaseAdminUser?: string;
  databasePasswordSsmParameter?: string;
  appDatabasePasswordSsmParameter?: string;
  readonlyDatabasePasswordSsmParameter?: string;
};

export type ExecutionResult = {
  mode: 'real';
  command: string;
  workingDirectory: string;
  startedAt: string;
  finishedAt?: string;
  exitCode?: number;
  summary: string;
  output: string;
};

export type SandboxFeatures = {
  apiEnabled: boolean;
  apiInstanceType: string;
  vpcCidr?: string;
  vpnEnabled?: boolean;
  frontendEnabled: boolean;
  lambdaEnabled: boolean;
  dbEnabled: boolean;
  extraS3Enabled: boolean;
  extraS3Name?: string;
  apiEnvVars: Record<string, string>;
  lambdaEnvVars: Record<string, string>;
  // Keys (within apiEnvVars/lambdaEnvVars) the owner marked as secret. Their
  // values still reach tfvars, but the API masks them on read (write-only): the
  // owner can replace a secret but never read it back.
  apiSecretKeys?: string[];
  lambdaSecretKeys?: string[];
  apiDeployFolder?: string;
  webDeployFolder?: string;
};

type SandboxLifecycle = {
  ttlOption: RetentionOption;
  autoDestroy: boolean;
  destroyAt?: string;
  destroyedAt?: string;
};

export type ProjectType = 'sandbox' | 'low-code';

type SandboxAccountRequest = {
  projectType: ProjectType;
  projectName: string;
  projectSlug: string;
  accountName: string;
  accountEmail: string;
  ownerEmail: string;
  organizationalUnitId: string;
  organizationalUnitName: string;
  accountId?: string;
  requestedOwnerName?: string;
  requestedOwnerSurname?: string;
};

export type SandboxRequest = {
  id: string;
  ownerEntityRef: string;
  owner: string;
  sandboxName: string;
  status: SandboxStatus;
  templateSource: string;
  createdAt: string;
  updatedAt: string;
  lastOperation: string;
  environment?: SandboxEnvironment;
  // How the signed-in user may use this sandbox (set by the backend): owner,
  // admin, or a `read` / `write` share granted from the Access admin page.
  access?: 'admin' | 'owner' | 'read' | 'write';
  // AWS spend of the account this sandbox runs in (Cost Explorer, refreshed a
  // few times a day). Account-level: `sharedWith` > 1 means several live
  // sandboxes share this bill.
  cost?: SandboxCost;
  features?: SandboxFeatures;
  account: SandboxAccountRequest;
  lifecycle: SandboxLifecycle;
  dnsPhase?: 'pending' | 'confirmed';
  autoProvision?: boolean;
  accountVendingArtifacts?: {
    workspaceDir: string;
    tfDataDir: string;
    tfvarsPath: string;
    planPath: string;
    accountKey: string;
    accountName: string;
    accountEmail: string;
    parentId: string;
  };
  generatedArtifacts?: {
    workspaceDir: string;
    backendConfigPath: string;
    tfvarsPath: string;
    stateStackName: string;
    stateKey: string;
    domainName: string;
  };
  outputs?: SandboxOutputs;
  accountPlanResult?: ExecutionResult;
  accountApplyResult?: ExecutionResult;
  planResult?: ExecutionResult;
  dnsResult?: ExecutionResult;
  applyResult?: ExecutionResult;
  destroyResult?: ExecutionResult;
};

// Portal-only dev/prod label set at creation. Informational: it does not
// change what gets provisioned.
export type SandboxEnvironment = 'dev' | 'prod';

export const ENVIRONMENT_OPTIONS: Array<{
  id: SandboxEnvironment;
  label: string;
  sub: string;
  color: string;
  tint: string;
}> = [
  { id: 'dev', label: 'Dev', sub: 'development / testing', color: '#5aa2e0', tint: 'rgba(90,162,224,.16)' },
  { id: 'prod', label: 'Prod', sub: 'production workload', color: '#e0a35a', tint: 'rgba(224,163,90,.16)' },
];

// Default CI deploy folder per environment: dev → "qa", prod → "prod".
export const defaultDeployFolder = (env?: SandboxEnvironment) =>
  env === 'prod' ? 'prod' : 'qa';

export const environmentMeta = (env?: SandboxEnvironment) =>
  ENVIRONMENT_OPTIONS.find(o => o.id === env);

export type SandboxCost = {
  accountId: string;
  currency: string;
  monthToDate: number;
  lastMonth: number;
  monthStart: string;
  sharedWith: number;
  fetchedAt: string;
};

export const formatCost = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: amount >= 100 ? 0 : 2,
  }).format(amount);

// Month-to-date total across the given sandboxes, counting each AWS account
// once (several sandboxes can share one account's bill).
export const totalMonthToDate = (requests: SandboxRequest[]) => {
  const byAccount = new Map<string, SandboxCost>();
  for (const r of requests) {
    if (r.cost) byAccount.set(r.cost.accountId, r.cost);
  }
  if (byAccount.size === 0) return undefined;
  const costs = [...byAccount.values()];
  return {
    amount: costs.reduce((sum, c) => sum + c.monthToDate, 0),
    currency: costs[0].currency,
  };
};

export type SandboxLimit = {
  ownerEntityRef: string;
  maxActiveRequests: number;
  activeRequests: number;
  remainingRequests: number;
};

export type EnvVarEntry = {
  id: string;
  key: string;
  value: string;
  isSecret?: boolean;
};

// Placeholder the API returns in place of a secret's real value. When the
// editor submits an unchanged secret, this same sentinel travels back and the
// backend keeps the stored value instead of overwriting it.
export const SECRET_MASK = '••••••';

type StatusTone = 'success' | 'error' | 'progress' | 'attention' | 'idle';

export const MACHINE_PROFILES = [
  {
    value: 't4g.micro',
    label: 'Starter',
    specs: '2 vCPU burst, 1 GiB RAM, Graviton',
  },
  {
    value: 't4g.small',
    label: 'Small',
    specs: '2 vCPU burst, 2 GiB RAM, Graviton',
  },
  {
    value: 't4g.medium',
    label: 'Balanced',
    specs: '2 vCPU burst, 4 GiB RAM, Graviton',
  },
  {
    value: 't4g.large',
    label: 'Large',
    specs: '2 vCPU, 8 GiB RAM, Graviton',
  },
  {
    value: 'm7g.large',
    label: 'Compute Large',
    specs: '2 vCPU, 8 GiB RAM, Graviton 3',
  },
];

export const RETENTION_OPTIONS: Array<{
  value: RetentionOption;
  label: string;
  description: string;
}> = [
  {
    value: '7d',
    label: '7 days',
    description: 'Default sandbox lifetime for quick experiments.',
  },
  {
    value: '30d',
    label: '30 days',
    description: 'Useful for feature branches or longer QA cycles.',
  },
  {
    value: '90d',
    label: '90 days',
    description: 'Keeps the environment alive for one quarter.',
  },
  {
    value: '365d',
    label: '1 year',
    description: 'Long-running project sandbox.',
  },
  {
    value: 'never',
    label: 'Do not delete',
    description: 'No automatic destroy. Manual cleanup is required.',
  },
];

export const STATUS_META: Record<
  SandboxStatus,
  { label: string; tone: StatusTone }
> = {
  requested: { label: 'Requested', tone: 'idle' },
  planning_account: { label: 'Validating account', tone: 'progress' },
  account_planned: { label: 'Account validated', tone: 'idle' },
  creating_account: { label: 'Creating account', tone: 'progress' },
  account_created: { label: 'Account ready', tone: 'idle' },
  bootstrapping_account: { label: 'Bootstrapping', tone: 'progress' },
  preparing_state: { label: 'Preparing state', tone: 'progress' },
  planning: { label: 'Planning', tone: 'progress' },
  planned: { label: 'Plan ready', tone: 'idle' },
  applying: { label: 'Applying', tone: 'progress' },
  awaiting_dns: { label: 'Action needed · DNS', tone: 'attention' },
  destroying: { label: 'Destroying', tone: 'progress' },
  ready: { label: 'Ready', tone: 'success' },
  failed: { label: 'Failed', tone: 'error' },
  destroyed: { label: 'Destroyed', tone: 'idle' },
};

// Outputs that map directly onto CI/CD pipeline variables. Surfaced as
// one-click copyable fields so they can be pasted into GitHub secrets.
export const DEPLOY_OUTPUT_LABELS = new Set([
  'ASG_NAME',
  'AWS_ROLE_ARN',
  'DEPLOY_S3_BUCKET',
  'ECR_URI',
  'AWS_DISTRIBUTION_ID',
  'AWS_S3_BUCKET',
]);

export const SANDBOX_ACCOUNTS_OU_ID = 'ou-lzq8-ip1apfiv';
export const LOW_CODE_OU_ID = 'ou-lzq8-8by30rt9';
export const ACCOUNT_VENDING_OWNER_EMAIL = 'doom.slayer@ninetwothree.co';

// Mirrors the backend rule: only a private /16 is accepted, because the
// Terraform template splits the block into /24 subnets and sandboxes attached
// to the same VPN must not overlap.
export const DEFAULT_VPC_CIDR = '10.1.0.0/16';

export const isValidVpcCidr = (value: string): boolean => {
  const match = /^(\d{1,3})\.(\d{1,3})\.0\.0\/16$/.exec(value.trim());
  if (!match) {
    return false;
  }
  const first = Number(match[1]);
  const second = Number(match[2]);
  if (first > 255 || second > 255) {
    return false;
  }
  return (
    first === 10 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168)
  );
};
const ACCOUNT_VENDING_EMAIL_DOMAIN = 'ninetwothree.co';
const ACCOUNT_VENDING_EMAIL_LOCAL_PART = 'doom.slayer';

export const sanitizeSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sanitizeEmailAlias = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const buildAccountName = (
  projectType: ProjectType,
  ownerName: string,
  ownerSurname: string,
  projectName: string,
) =>
  projectType === 'low-code'
    ? `lc-${sanitizeSlug(projectName)}`
    : `sandbox-${sanitizeSlug(ownerName)}-${sanitizeSlug(
        ownerSurname,
      )}`.replace(/-+$/g, '');

export const buildAccountEmailAlias = (
  projectType: ProjectType,
  ownerName: string,
  ownerSurname: string,
  projectName: string,
) => {
  if (projectType === 'low-code') {
    return sanitizeSlug(projectName) || 'project';
  }

  return (
    [sanitizeEmailAlias(ownerName), sanitizeEmailAlias(ownerSurname)]
      .filter(Boolean)
      .join('_') || 'sandbox'
  );
};

export const buildAccountEmail = (alias: string) =>
  `${ACCOUNT_VENDING_EMAIL_LOCAL_PART}+${alias}@${ACCOUNT_VENDING_EMAIL_DOMAIN}`;

export const isActiveStatus = (status: SandboxStatus) =>
  status === 'planning_account' ||
  status === 'creating_account' ||
  status === 'bootstrapping_account' ||
  status === 'preparing_state' ||
  status === 'planning' ||
  status === 'applying' ||
  status === 'destroying';

// Terminal states: the pipeline has come to rest and nothing more will change
// on its own. Everything else — including the brief "between stage" statuses
// (requested, account_created, planned, awaiting_dns, …) — means work is still
// in flight, so the UI must keep polling for live log/status updates. Using this
// (rather than isActiveStatus) for the poll trigger is what keeps logs updating
// live across stage transitions without a manual page refresh.
export const isTerminalStatus = (status: SandboxStatus) =>
  status === 'ready' || status === 'failed' || status === 'destroyed';

export const createEnvVarEntry = (): EnvVarEntry => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  key: '',
  value: '',
  isSecret: false,
});

export const envVarEntriesToRecord = (entries: EnvVarEntry[]) =>
  Object.fromEntries(
    entries
      .map(entry => [entry.key.trim(), entry.value.trim()])
      .filter(([key]) => key.length > 0),
  );

// The keys the owner flagged as secret — sent alongside the value record so the
// backend knows which values to mask on read.
export const envVarEntriesToSecretKeys = (entries: EnvVarEntry[]): string[] =>
  entries
    .filter(entry => entry.isSecret && entry.key.trim().length > 0)
    .map(entry => entry.key.trim());

export const recordToEnvVarEntries = (
  record: Record<string, string>,
  secretKeys: string[] = [],
): EnvVarEntry[] => {
  const secret = new Set(secretKeys);
  return Object.entries(record).map(([key, value]) => ({
    ...createEnvVarEntry(),
    key,
    value,
    isSecret: secret.has(key),
  }));
};

// Parse a .env file. Supports `KEY=VALUE`, `export KEY=VALUE`, comments (#),
// blank lines, and single/double-quoted values (quotes are stripped).
export const parseDotEnv = (text: string): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) {
      continue;
    }
    const withoutExport = line.replace(/^export\s+/, '');
    const eq = withoutExport.indexOf('=');
    if (eq <= 0) {
      continue;
    }
    const key = withoutExport.slice(0, eq).trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
      continue;
    }
    let value = withoutExport.slice(eq + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    result[key] = value;
  }
  return result;
};

// Overlay parsed .env entries onto whatever the user already typed, keyed by
// name so re-importing updates values without dropping manual edits.
export const mergeEnvEntries = (
  existing: EnvVarEntry[],
  parsed: Record<string, string>,
): EnvVarEntry[] => {
  const merged = { ...envVarEntriesToRecord(existing), ...parsed };
  const entries = recordToEnvVarEntries(merged);
  return entries.length > 0 ? entries : [createEnvVarEntry()];
};

export type EnvVarScope = 'api' | 'lambda';

export type ReservedEnvFeatures = {
  dbEnabled?: boolean;
  extraS3Enabled?: boolean;
  extraS3Name?: string;
};

// Env var names the sandbox infrastructure injects automatically based on the
// selected features. Users must not override these (the running app would pick
// up the wrong value and break), so they are blocked in the editor and skipped
// on import. Kept in sync with the backend `reservedEnvNames` helper and with
// template/managed_asg.tf + template/locals.tf.
export const reservedEnvNames = (
  features: ReservedEnvFeatures | undefined,
  scope: EnvVarScope,
): Set<string> => {
  const names = new Set<string>();
  const add = (...list: string[]) =>
    list.forEach(name => names.add(name.toUpperCase()));

  // Always managed by the infra runtime / userdata.
  add('NODE_ENV', 'HOST', 'PORT', 'DEFAULT_PORT', 'DB_NAME');
  if (scope === 'api') {
    add(
      'FRONTEND_URL',
      'FE_URL',
      'ML_URL',
      'ML_API_URL',
      'SERVER_URL',
      'API_URL',
    );
  } else {
    add('FE_URL');
  }
  // The DB checkbox also provisions a bastion, which injects the proxy vars.
  if (features?.dbEnabled) {
    add('DB_HOST', 'DB_PORT', 'DB_USER', 'DB_PASSWORD');
    add('NO_PROXY', 'HTTP_PROXY', 'HTTPS_PROXY');
  }
  // The extra S3 bucket only injects the files.* vars when it is named "files".
  if (
    features?.extraS3Enabled &&
    (features.extraS3Name ?? '').trim().toLowerCase() === 'files'
  ) {
    add(
      'AWS_S3_BUCKET',
      'AWS_STORAGE_BUCKET_NAME',
      'FILES_CDN_URL',
      'AWS_S3_CUSTOM_DOMAIN',
    );
  }
  return names;
};

export const isReservedEnvName = (
  name: string,
  features: ReservedEnvFeatures | undefined,
  scope: EnvVarScope,
): boolean => reservedEnvNames(features, scope).has(name.trim().toUpperCase());

export const reservedEnvKeysInEntries = (
  entries: EnvVarEntry[],
  features: ReservedEnvFeatures | undefined,
  scope: EnvVarScope,
): string[] =>
  entries
    .map(entry => entry.key.trim())
    .filter(key => key.length > 0 && isReservedEnvName(key, features, scope));

// Split a parsed .env record into the allowed part and the reserved names that
// must be skipped on import.
export const splitReservedEnv = (
  record: Record<string, string>,
  features: ReservedEnvFeatures | undefined,
  scope: EnvVarScope,
): { allowed: Record<string, string>; skipped: string[] } => {
  const allowed: Record<string, string> = {};
  const skipped: string[] = [];
  for (const [key, value] of Object.entries(record)) {
    if (isReservedEnvName(key, features, scope)) {
      skipped.push(key);
    } else {
      allowed[key] = value;
    }
  }
  return { allowed, skipped };
};

export const formatDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : 'Not scheduled';

export const retentionLabel = (option: RetentionOption) =>
  RETENTION_OPTIONS.find(item => item.value === option)?.label ?? option;

export const entityNameFromRef = (entityRef?: string) =>
  entityRef?.split('/').at(-1) || 'guest';

export const getDestroyCountdown = (destroyAt?: string, nowMs = Date.now()) => {
  if (!destroyAt) {
    return 'Not scheduled';
  }

  const destroyAtMs = new Date(destroyAt).getTime();

  if (Number.isNaN(destroyAtMs)) {
    return 'Not scheduled';
  }

  const remainingMs = destroyAtMs - nowMs;

  if (remainingMs <= 0) {
    return 'Destroy overdue';
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
};

export const getDestroyCountdownTone = (
  destroyAt?: string,
  nowMs = Date.now(),
) => {
  if (!destroyAt) {
    return 'idle' as const;
  }

  const destroyAtMs = new Date(destroyAt).getTime();

  if (Number.isNaN(destroyAtMs)) {
    return 'idle' as const;
  }

  const remainingMs = destroyAtMs - nowMs;

  if (remainingMs <= 0) {
    return 'expired' as const;
  }

  if (remainingMs <= 24 * 60 * 60 * 1000) {
    return 'critical' as const;
  }

  if (remainingMs <= 3 * 24 * 60 * 60 * 1000) {
    return 'warning' as const;
  }

  return 'normal' as const;
};

export const outputEntries = (
  outputs?: SandboxOutputs,
): Array<[string, string]> => {
  if (!outputs) {
    return [];
  }

  return (
    [
      ['ASG_NAME', outputs.managedAsgName],
      ['AWS_ROLE_ARN', outputs.githubRoleArn],
      ['DEPLOY_S3_BUCKET', outputs.deployBucket],
      ['ECR_URI', outputs.ecrUri],
      ['AWS_DISTRIBUTION_ID', outputs.cloudfrontDistributionId],
      ['AWS_S3_BUCKET', outputs.webBucket],
      ['State key', outputs.stateKey],
      ['TF vars', outputs.tfvarsPath],
      ['Primary sandbox URL', outputs.sandboxUrl],
      ['Managed ASG DNS', outputs.managedAsgDnsName],
      ['Lambda ARN', outputs.lambdaArn],
      ['Bastion instance ID', outputs.bastionInstanceId],
      ['Bastion public IP', outputs.bastionPublicIp],
      ['Bastion private IP', outputs.bastionPrivateIp],
      ['Bastion SSM target', outputs.bastionSsmTarget],
      ['DB endpoint', outputs.databaseEndpoint],
      ['DB port', outputs.databasePort],
      ['DB name', outputs.databaseName],
      ['DB admin user', outputs.databaseAdminUser],
      ['DB password SSM', outputs.databasePasswordSsmParameter],
      ['DB app password SSM', outputs.appDatabasePasswordSsmParameter],
      [
        'DB readonly password SSM',
        outputs.readonlyDatabasePasswordSsmParameter,
      ],
    ] as Array<[string, string | undefined]>
  ).filter((entry): entry is [string, string] => Boolean(entry[1]));
};
